/**
 * Client billing period helpers — yearly ↔ monthly conversion and pending calculations.
 */

export type PaymentType = 'Yearly' | 'Monthly';

export interface BillingClientFields {
  paymentType?: PaymentType | string | null;
  signupDate?: string | null;
  yearlyBillingStartDate?: string | null;
  monthlyBillingStartDate?: string | null;
  totalPrice?: number | null;
  firstPayment?: number | null;
  monthlyPayment?: number | null;
  splitPayments?: boolean | null;
  paymentDueDate?: string | null;
}

export interface BillingTransaction {
  transactionDate?: string | Date | null;
  amount?: number | null;
  transaction_amount?: number | null;
  vat_amount?: number | null;
  stripeProcessingFee?: number | null;
  transactionType?: string | null;
}

/** Calendar date key YYYY-MM-DD (local timezone). */
export function toDateKey(value: string | Date): string {
  const d = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(`${value}T12:00:00`)
    : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Whether a transaction counts toward the active monthly billing period. */
export function isTransactionInMonthlyPeriod(
  tx: BillingTransaction,
  billingStart: string | Date,
  asOf: Date = new Date()
): boolean {
  if (!tx.transactionDate) return false;
  const txDate = new Date(tx.transactionDate);
  const start = new Date(billingStart);
  if (Number.isNaN(txDate.getTime()) || Number.isNaN(start.getTime())) return false;
  if (txDate > asOf) return false;

  const txKey = toDateKey(txDate);
  const startKey = toDateKey(start);
  if (txKey >= startKey) return true;

  // Renewal paid in the same month as billing start (e.g. paid 7 Jul, start set 8 Jul on conversion)
  const sameMonth =
    txDate.getFullYear() === start.getFullYear() && txDate.getMonth() === start.getMonth();
  if (!sameMonth) return false;

  const type = tx.transactionType || 'firstPayment';
  return type === 'renewal' || type === 'firstPayment';
}

export function sumMonthlyPeriodTransactions(
  transactions: BillingTransaction[],
  billingStart: string | Date | null,
  asOf: Date = new Date()
): number {
  if (!billingStart) return 0;
  return transactions.reduce((sum, tx) => {
    if (!isTransactionInMonthlyPeriod(tx, billingStart, asOf)) return sum;
    return sum + getTransactionTotal(tx);
  }, 0);
}

export interface PaymentStatusResult {
  isPaymentPending: boolean;
  pendingAmount: number;
  totalExpected: number;
  paidInPeriod: number;
  billingStartDate: string | null;
}

/** Months elapsed from start date (inclusive of current period when day reached). */
export function countBillingMonths(startDate: string | Date, asOf: Date = new Date()): number {
  const start = new Date(startDate);
  if (Number.isNaN(start.getTime())) return 1;

  const yearDiff = asOf.getFullYear() - start.getFullYear();
  const monthDiff = asOf.getMonth() - start.getMonth();
  const baseMonths = yearDiff * 12 + monthDiff;
  const startDay = start.getDate();
  const currentDay = asOf.getDate();
  const monthsElapsed = currentDay >= startDay ? baseMonths + 1 : baseMonths;
  return Math.max(1, monthsElapsed);
}

/** Yearly periods elapsed from start (inclusive when anniversary reached). */
export function countBillingYears(startDate: string | Date, asOf: Date = new Date()): number {
  const start = new Date(startDate);
  if (Number.isNaN(start.getTime())) return 1;

  let years = asOf.getFullYear() - start.getFullYear();
  const anniversaryMonth = start.getMonth();
  const anniversaryDay = start.getDate();
  const passed =
    asOf.getMonth() > anniversaryMonth ||
    (asOf.getMonth() === anniversaryMonth && asOf.getDate() >= anniversaryDay);
  return Math.max(1, years + (passed ? 1 : 0));
}

export function resolvePaymentType(client: BillingClientFields): PaymentType {
  if (client.paymentType === 'Monthly' || client.paymentType === 'Yearly') {
    return client.paymentType;
  }
  if ((client.monthlyPayment || 0) > 0) return 'Monthly';
  return 'Yearly';
}

export function getYearlyBillingStart(client: BillingClientFields): string | null {
  return client.yearlyBillingStartDate || client.signupDate || null;
}

export function getMonthlyBillingStart(client: BillingClientFields): string | null {
  return client.monthlyBillingStartDate || client.signupDate || null;
}

export function deriveMonthlyFromYearly(yearlyAmount: number): number {
  if (!yearlyAmount || yearlyAmount <= 0) return 0;
  return Math.round((yearlyAmount / 12) * 100) / 100;
}

export function deriveYearlyFromMonthly(monthlyAmount: number): number {
  if (!monthlyAmount || monthlyAmount <= 0) return 0;
  return Math.round(monthlyAmount * 12 * 100) / 100;
}

export function getMonthlyExpectedToDate(
  client: BillingClientFields,
  asOf: Date = new Date()
): number {
  const start = getMonthlyBillingStart(client);
  if (!start) return 0;

  const months = countBillingMonths(start, asOf);
  const firstPayment = client.firstPayment || client.monthlyPayment || 0;
  const monthlyPayment = client.monthlyPayment || 0;
  return firstPayment + Math.max(0, months - 1) * monthlyPayment;
}

export function getYearlyExpectedToDate(
  client: BillingClientFields,
  asOf: Date = new Date()
): number {
  const yearlyPrice = client.totalPrice || 0;
  if (yearlyPrice <= 0) return 0;

  const start = getYearlyBillingStart(client);
  if (!start) return yearlyPrice;

  const periods = countBillingYears(start, asOf);
  return yearlyPrice * periods;
}

export function getTransactionTotal(tx: BillingTransaction): number {
  const base = tx.transaction_amount ?? tx.amount ?? 0;
  const vat = tx.vat_amount ?? 0;
  const stripe = tx.stripeProcessingFee ?? 0;
  return base + vat + stripe;
}

export function sumTransactions(
  transactions: BillingTransaction[],
  options?: { onOrAfter?: string | Date | null }
): number {
  const cutoffKey = options?.onOrAfter ? toDateKey(options.onOrAfter) : null;

  return transactions.reduce((sum, tx) => {
    if (cutoffKey) {
      if (!tx.transactionDate) return sum;
      const txKey = toDateKey(tx.transactionDate);
      if (!txKey || txKey < cutoffKey) return sum;
    }
    return sum + getTransactionTotal(tx);
  }, 0);
}

export function calculatePaymentStatus(
  client: BillingClientFields,
  transactions: BillingTransaction[],
  asOf: Date = new Date()
): PaymentStatusResult {
  const paymentType = resolvePaymentType(client);
  const allPaid = sumTransactions(transactions);

  if (paymentType === 'Monthly') {
    const billingStart = getMonthlyBillingStart(client);
    const paidInPeriod = sumMonthlyPeriodTransactions(transactions, billingStart, asOf);
    const totalExpected = getMonthlyExpectedToDate(client, asOf);
    const pendingAmount = Math.max(0, totalExpected - paidInPeriod);

    return {
      isPaymentPending: pendingAmount > 0,
      pendingAmount,
      totalExpected,
      paidInPeriod,
      billingStartDate: billingStart,
    };
  }

  // Yearly
  const contractTotal = client.totalPrice || 0;
  const billingStart = getYearlyBillingStart(client);

  if (client.splitPayments && client.paymentDueDate) {
    const dueDate = new Date(client.paymentDueDate);
    const pendingAmount = Math.max(0, contractTotal - allPaid);
    return {
      isPaymentPending: asOf > dueDate && pendingAmount > 0,
      pendingAmount,
      totalExpected: contractTotal,
      paidInPeriod: allPaid,
      billingStartDate: billingStart,
    };
  }

  const totalExpected = getYearlyExpectedToDate(client, asOf);
  const pendingAmount = Math.max(0, totalExpected - allPaid);

  return {
    isPaymentPending: pendingAmount > 0,
    pendingAmount,
    totalExpected,
    paidInPeriod: allPaid,
    billingStartDate: billingStart,
  };
}

export function toDateInputValue(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function todayDateInputValue(): string {
  return toDateInputValue(new Date().toISOString());
}
